import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useParams } from "@tanstack/react-router";
import { ChevronRight, Home } from "lucide-react";
import { api } from "../../lib/api";

interface Crumb {
  label: string;
  to?: string;
}

/**
 * B5.2 — Dynamic topbar breadcrumb.
 *
 * Renders a small breadcrumb just under the main topbar that reflects the
 * current route. For docs and collections it fetches the entity to show
 * the human title; for other routes it uses the path segments directly.
 */
export function TopBreadcrumb() {
  const loc = useLocation();
  const params = useParams({ strict: false }) as {
    docId?: string;
    slug?: string;
    notebookId?: string;
  };

  const docQuery = useQuery({
    queryKey: ["doc-meta", params.docId],
    queryFn: () => (params.docId ? api.getDoc(params.docId) : Promise.resolve(null)),
    enabled: !!params.docId,
    staleTime: 60_000,
  });
  const collectionQuery = useQuery({
    queryKey: ["collection-meta", params.slug],
    queryFn: () => (params.slug ? api.getCollection(params.slug) : Promise.resolve(null)),
    enabled: !!params.slug,
    staleTime: 60_000,
  });
  const notebookQuery = useQuery({
    queryKey: ["notebook-meta", params.notebookId],
    queryFn: () => (params.notebookId ? api.getNotebook(params.notebookId) : Promise.resolve(null)),
    enabled: !!params.notebookId,
    staleTime: 60_000,
  });

  const crumbs = buildCrumbs(loc.pathname, params, {
    docTitle: docQuery.data?.title ?? null,
    docProject: docQuery.data?.project ?? null,
    collectionTitle: collectionQuery.data?.title ?? null,
    notebookTitle: notebookQuery.data?.title ?? null,
  });

  // Hide on root home (no crumbs other than self)
  if (loc.pathname === "/") return null;

  return (
    <nav
      data-testid="top-breadcrumb"
      aria-label="Ruta"
      className="flex items-center gap-1 overflow-x-auto whitespace-nowrap border-b border-(--border-subtle) bg-(--bg-elevated) px-3 py-1 text-xs text-(--text-tertiary)"
    >
      <Link
        to="/"
        className="inline-flex items-center gap-1 hover:text-(--text-primary)"
        data-testid="breadcrumb-home"
      >
        <Home size={11} />
      </Link>
      {crumbs.map((c, idx) => (
        <span key={`${idx}-${c.label}`} className="inline-flex items-center gap-1">
          <ChevronRight size={10} />
          {c.to ? (
            <a
              href={c.to}
              className="hover:text-(--text-primary)"
              data-testid={`breadcrumb-${idx}`}
            >
              {c.label}
            </a>
          ) : (
            <span
              className="truncate text-(--text-secondary)"
              data-testid={`breadcrumb-${idx}`}
              style={{ maxWidth: 320 }}
            >
              {c.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}

function buildCrumbs(
  pathname: string,
  params: { docId?: string; slug?: string; notebookId?: string },
  resolved: {
    docTitle: string | null;
    docProject: string | null;
    collectionTitle: string | null;
    notebookTitle: string | null;
  },
): Crumb[] {
  const segs = pathname.split("/").filter(Boolean);
  if (segs.length === 0) return [];

  if (segs[0] === "docs") {
    const crumbs: Crumb[] = [{ label: "Documentos", to: "/docs" }];
    if (params.docId) {
      if (resolved.docProject) crumbs.push({ label: resolved.docProject });
      crumbs.push({ label: resolved.docTitle ?? params.docId });
      if (segs[2] === "edit") crumbs.push({ label: "Editar" });
    }
    return crumbs;
  }

  if (segs[0] === "collections") {
    const crumbs: Crumb[] = [{ label: "Colecciones", to: "/collections" }];
    if (params.slug) crumbs.push({ label: resolved.collectionTitle ?? params.slug });
    return crumbs;
  }

  if (segs[0] === "notebooks") {
    const crumbs: Crumb[] = [{ label: "Apuntes", to: "/notebooks" }];
    if (params.notebookId) crumbs.push({ label: resolved.notebookTitle ?? params.notebookId });
    return crumbs;
  }

  if (segs[0] === "search") return [{ label: "Buscar" }];
  if (segs[0] === "chat") return [{ label: "Chat" }];
  if (segs[0] === "highlights") return [{ label: "Highlights" }];
  if (segs[0] === "graph") return [{ label: "Graph" }];
  if (segs[0] === "upload") return [{ label: "Importar" }];
  if (segs[0] === "settings") {
    const crumbs: Crumb[] = [{ label: "Ajustes", to: "/settings" }];
    if (segs[1]) crumbs.push({ label: capitalize(segs[1]) });
    return crumbs;
  }
  if (segs[0] === "admin") {
    const crumbs: Crumb[] = [{ label: "Admin" }];
    if (segs[1]) crumbs.push({ label: capitalize(segs[1]) });
    return crumbs;
  }

  // Default: capitalize each segment
  return segs.map((s) => ({ label: capitalize(s) }));
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
